import { useEffect, useRef, useState } from "react";
import { AiActionCard } from "./AiActionCard";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, MessageSquare, Check, RefreshCw, Link2, FileCheck2, Target, UserPlus, Sparkles, ChevronRight, Wand2, Send, Users, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { ManagedCampaignData } from "@/components/managed-campaign/ManagedCampaignData";
import { useAudienceContext } from "@/hooks/useAudienceContext";
import { AiGenerateMessagesCard } from "./AiGenerateMessagesCard";
import { TICKET_BANDS } from "@/lib/profileCompleteness";

interface Props {
  companyProfileId: string;
  campaignKnowledgeId: string;
  onGenerated: (data: Partial<ManagedCampaignData>) => void;
  hasGenerated?: boolean;
  isPremium?: boolean;
  currentSequences?: ManagedCampaignData["sequences"];
  /** Current audience tab values; messages are written for these, not the first AI draft. */
  audienceFilters?: Record<string, unknown>;
  guestToken?: string;
  workspaceId?: string | null;
}

async function call(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke("company-profile-builder", { body });
  if (error) {
    let msg = error.message;
    try { const ctx = await (error as any).context?.json?.(); if (ctx?.error) msg = ctx.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

/** Phase 4: messages built from the campaign's saved facts; falls back to the old card for old profiles. */
export function MessagesBuilderCard(props: Props) {
  const { companyProfileId, campaignKnowledgeId, onGenerated, hasGenerated, isPremium, guestToken, workspaceId } = props;
  const base = { workspaceId: guestToken ? null : workspaceId ?? null, guestToken: guestToken ?? null, profileId: companyProfileId, campaignKnowledgeId };
  const [isV2, setIsV2] = useState<boolean | null>(true);
  const [keyFacts, setKeyFacts] = useState<string[]>([]);
  const [catalogue, setCatalogue] = useState("");
  const [startMode, setStartMode] = useState<"note" | "no_note" | "connected">("note");
  const [tone, setTone] = useState("Professional");
  const [count, setCount] = useState(2);
  const [ctx, setCtx] = useState<{ products: { name: string; about?: string; ticket_size?: string }[]; ticket: string; pricing: { disclose?: boolean; mode?: string; text?: string } | null; countries: string[]; goal?: string }>({ products: [], ticket: "", pricing: null, countries: [] });
  const [notes, setNotes] = useState("");
  const [goal, setGoal] = useState<{ ask: string; details: Record<string, string>; reach: string }>({ ask: "", details: {}, reach: "new" });
  const [createOpen, setCreateOpen] = useState(false);
  const [improveOpen, setImproveOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [improved, setImproved] = useState<{ sequences: any[]; changes: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<{ sequences: any[]; why: string } | null>(null);
  const [done, setDone] = useState(!!hasGenerated);

  const q = useAudienceContext(base, campaignKnowledgeId);
  useEffect(() => {
    const r = q.data;
    if (!r) return;
    setIsV2(!!r.isV2); setKeyFacts(r.keyFacts ?? []); setCatalogue(r.catalogueUrl ?? ""); setGoal({ ask: r.firstAsk ?? "", details: r.askDetails ?? {}, reach: r.reach ?? "new" }); setTone(r.tone || "Professional"); setStartMode(r.reach === "existing" ? "connected" : r.startMode === "no_note" ? "no_note" : "note");
        const c = r.confirmed ?? {};
        const products = (Array.isArray(r.productDetails) ? r.productDetails : []) as { name: string; about?: string; ticket_size?: string }[];
        const bands = products.map((p) => String(p.ticket_size ?? "")).filter((t) => t && t !== "Not sure" && TICKET_BANDS.includes(t));
        const ticket = bands.sort((a: string, b: string) => TICKET_BANDS.indexOf(b) - TICKET_BANDS.indexOf(a))[0] ?? String(c.ticket_size ?? "");
        setCtx({ products, ticket, pricing: c.pricing ?? null, countries: Array.isArray(c.countries) ? c.countries : [], goal: c.goal === "other" ? c.goal_custom : ({ sell: "Sell a product or service", partners: "Find partners or distributors", agency: "Get clients for my agency", hiring: "Hire people", networking: "Networking", event: "Promote an event or webinar", job_search: "Job search", investors: "Raise funding" } as Record<string, string>)[c.goal] });
        const tier = ["Under ₹50k", "₹50k – 2 lakh"].includes(ticket) ? 2 : ["₹2 – 10 lakh", "₹10 – 50 lakh"].includes(ticket) ? 3 : ["₹50 lakh – 1 crore", "Above ₹1 crore"].includes(ticket) ? 5 : 0;
        if (tier) setCount(tier);
  }, [q.data]);
  useEffect(() => { if (q.isError && !q.data) setIsV2(false); }, [q.isError, q.data]);

  useEffect(() => { setDone(!!hasGenerated); }, [hasGenerated]);

  const sequenceMessages = (props.currentSequences ?? []).filter((x: any) => (x.action_type === "message" || x.action_type === "connect") && String(x.message_template ?? "").trim());
  // Fill in the sequence prepared when the strategy was saved, unless the user already has messages.
  const applied = useRef(false);
  const prepared = q.data?.preparedSequences as any[] | null | undefined;
  useEffect(() => {
    if (applied.current || q.isFetching || !prepared?.length) return;
    applied.current = true;
    if (sequenceMessages.length || hasGenerated) return;
    onGenerated({ sequences: prepared });
    setDone(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepared, q.isFetching]);
  const preparing = !done && !sequenceMessages.length && !prepared?.length && !!q.data?.preparing;

  if (isV2 === null) return <Card><CardContent className="p-5 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</CardContent></Card>;
  if (!isV2) return <AiGenerateMessagesCard {...props} />;
  if (preparing) return <Card><CardContent className="p-5 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin text-primary" /> Preparing your messages…</CardContent></Card>;

  const generate = async () => {
    setBusy(true);
    try {
      const keys = ["countries", "cities", "industries", "companySizes", "seniorityLevels", "departments", "jobTitles", "companyKeywords", "personKeywords", "excludeCompanyKeywords", "excludePersonKeywords"];
      const filters = props.audienceFilters ? Object.fromEntries(keys.map((k) => [k, props.audienceFilters?.[k] ?? []])) : undefined;
      const r = await call({
        ...base, action: "audience_messages", startMode, includeConnectionRequest: startMode !== "connected",
        isPremium: !!isPremium, filters,
      });
      setDraft({ sequences: r.sequences, why: r.explanation ?? "" });
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const improve = async () => {
    setBusy(true);
    try {
      const keys = ["countries", "cities", "industries", "companySizes", "seniorityLevels", "departments", "jobTitles", "companyKeywords", "personKeywords", "excludeCompanyKeywords", "excludePersonKeywords"];
      const filters = props.audienceFilters ? Object.fromEntries(keys.map((k) => [k, props.audienceFilters?.[k] ?? []])) : undefined;
      const r = await call({
        ...base, action: "audience_improve", currentSequences: props.currentSequences ?? [], instruction: instruction.trim(),
        tone, isPremium: !!isPremium, filters,
      });
      setImproved({ sequences: r.sequences, changes: r.explanation ?? "" });
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const useImproved = () => {
    if (!improved) return;
    onGenerated({ sequences: improved.sequences });
    setImproveOpen(false); setImproved(null); setInstruction("");
    toast.success("Improved messages added. You can still edit them below.");
  };

  const IMPROVE_CHIPS = ["Shorter", "More personal", "Softer ask", "Stronger opening", "Mention a client", "Less salesy"];
  const stepLabel = (x: any, i: number) => x.action_type === "connect" ? "Connection note" : `Message ${i}`;
  const Preview = ({ seq }: { seq: any[] }) => {
    let m = 0;
    return (
      <div className="space-y-2">
        {seq.filter((x: any) => String(x.message_template ?? "").trim()).map((x: any, idx: number) => (
          <div key={idx} className="rounded-lg border bg-muted/30 p-3">
            <div className="mb-1 text-xs font-medium text-muted-foreground">{stepLabel(x, x.action_type === "message" ? ++m : m)}</div>
            <p className="whitespace-pre-wrap text-sm">{x.message_template}</p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {done ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="mr-auto inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <span className="h-2 w-2 rounded-full bg-success" />
            Ready · {sequenceMessages.length} step{sequenceMessages.length === 1 ? "" : "s"} · click any step to edit
          </span>
          {sequenceMessages.length > 0 && (
            <Button type="button" size="sm" variant="outline" className="gap-1.5"
              onClick={() => { setImproved(null); setImproveOpen(true); }}>
              <Wand2 className="h-4 w-4 text-primary" /> Suggest improvements
            </Button>
          )}
          <Button type="button" size="sm" variant="ghost" className="gap-1.5"
            onClick={() => { if (window.confirm("Create a new sequence? This replaces the current one.")) { setDraft(null); setCreateOpen(true); } }}>
            <MessageSquareText className="h-4 w-4" /> Create again
          </Button>
        </div>
      ) : (
        <AiActionCard
          icon={MessageSquareText}
          title="Create my sequence"
          subtitle="Written from your business and campaign"
          actionLabel="Create sequence"
          onClick={() => { setDraft(null); setCreateOpen(true); }}
          state="idle"
        />
      )}


      <Dialog open={createOpen} onOpenChange={(o) => !busy && setCreateOpen(o)}>
        <DialogContent className="flex max-h-[90vh] max-w-xl flex-col gap-0 overflow-hidden p-0">
          <div className="border-b px-6 pt-5 pb-4">
            <DialogTitle className="text-lg font-semibold">{draft ? "Your sequence" : "Create your sequence"}</DialogTitle>
            <DialogDescription asChild>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                <Target className="h-3.5 w-3.5 text-primary" />
                <span className="font-medium text-foreground">{goal.ask || "Reply"}</span>
                {[goal.details.duration && `${goal.details.duration}`, goal.details.phone, goal.details.link && goal.details.link.replace(/^https?:\/\//, ""), ctx.countries.length ? ctx.countries.join(", ") : "", goal.reach === "existing" ? "Existing connections" : "New people"]
                  .filter(Boolean).map((t) => <span key={String(t)} className="before:mr-2 before:content-['·']">{t}</span>)}
              </div>
            </DialogDescription>
          </div>
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {busy ? (
              <div className="flex flex-col items-center gap-3 py-12"><Loader2 className="h-7 w-7 animate-spin text-primary" /><p className="text-sm text-muted-foreground">Writing your messages…</p></div>
            ) : draft ? (
              <>
                {draft.why && <p className="rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground"><Sparkles className="mr-1 inline h-3.5 w-3.5 text-primary" />{draft.why}</p>}
                <Preview seq={draft.sequences} />
              </>
            ) : goal.reach === "existing" ? (
              <p className="text-sm text-muted-foreground">These people are already your connections, so we'll message them directly. The AI picks the tone, number of follow-ups and timing from your business and campaign.</p>
            ) : (
              <div className="space-y-3">
                <div className="text-sm font-semibold">How should we connect?</div>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { v: "note", icon: UserPlus, label: "Invite with a note" },
                    { v: "no_note", icon: Send, label: "Invite without a note" },
                  ] as const).map((o) => {
                    const on = startMode === o.v;
                    return (
                      <button key={o.v} type="button" onClick={() => setStartMode(o.v)}
                        className={cn("relative flex flex-col items-center gap-1.5 rounded-lg border-2 px-2 py-4 text-center text-sm font-medium transition-colors", on ? "border-sage bg-sage-muted text-foreground" : "border-border text-muted-foreground hover:border-primary/40")}>
                        {on && <Check className="absolute right-1.5 top-1.5 h-3.5 w-3.5 text-sage" />}
                        <o.icon className={cn("h-5 w-5", on ? "text-sage" : "")} />{o.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">The AI picks the tone, number of follow-ups and timing from your business and campaign.</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t px-6 py-3">
            {draft && !busy ? (
              <>
                <Button variant="outline" onClick={generate} className="gap-2"><RefreshCw className="h-4 w-4" /> Try again</Button>
                <Button onClick={() => { onGenerated({ sequences: draft.sequences }); setDone(true); setCreateOpen(false); setDraft(null); toast.success("Sequence added. Click any step to edit it."); }} className="gap-2"><Check className="h-4 w-4" /> Use this sequence</Button>
              </>
            ) : (
              <>
                <Button variant="outline" disabled={busy} onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button onClick={generate} disabled={busy} className="gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Create sequence</Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={improveOpen} onOpenChange={(o) => !busy && setImproveOpen(o)}>
        <DialogContent className="flex max-h-[90vh] max-w-xl flex-col gap-0 overflow-hidden p-0">
          <div className="border-b px-6 pt-5 pb-4">
            <DialogTitle className="text-lg font-semibold">{improved ? "Improved messages" : "What would you like to improve?"}</DialogTitle>
            <DialogDescription className="mt-1">{improved ? (improved.changes || "Review the new version before using it.") : "We'll refine the messages you have now, including any edits you made."}</DialogDescription>
          </div>
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {busy ? (
              <div className="flex flex-col items-center gap-3 py-12"><Loader2 className="h-7 w-7 animate-spin text-primary" /><p className="text-sm text-muted-foreground">Improving your messages…</p></div>
            ) : improved ? (
              <Preview seq={improved.sequences} />
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {IMPROVE_CHIPS.map((c) => (
                    <button key={c} type="button" onClick={() => setInstruction((v) => (v.trim() ? `${v.trim()}, ${c.toLowerCase()}` : c))}
                      className="rounded-full border-2 px-3 py-1 text-sm transition-colors hover:border-primary/40">+ {c}</button>
                  ))}
                </div>
                <Textarea autoFocus value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={3} placeholder="e.g. make message 2 shorter and mention our Emami case study" />
                <div className="text-xs font-medium text-muted-foreground">Your current sequence</div>
                <Preview seq={props.currentSequences ?? []} />
              </>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t px-6 py-3">
            {improved ? (
              <>
                <Button variant="outline" onClick={() => setImproved(null)}>Cancel</Button>
                <Button onClick={useImproved} className="gap-2"><Check className="h-4 w-4" /> Use these</Button>
              </>
            ) : (
              <>
                <Button variant="outline" disabled={busy} onClick={() => setImproveOpen(false)}>Cancel</Button>
                <Button onClick={improve} disabled={busy || !instruction.trim()} className="gap-2"><Wand2 className="h-4 w-4" /> Improve messages</Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Section({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">{n}</span>{title}
      </div>
      <div className="space-y-2 pl-7">{children}</div>
    </div>
  );
}
